import { useMemo } from 'react';

import { buildActionSet } from '@/authz/authority';
import { toRoleActionsValue } from '@/authz/context-value';
import { useJwtSnapshot } from '@/authz/token-store';
import { type IPermissionSnapshot, type PermissionSource } from '@/authz/types';
import { ISADMINLOGGED } from '@/constants/global';
import type { ACTIONTYPE } from '@/layout/permissions/actions';
import type { IUseIsRole } from '@/layout/utils';
import { useGetForMultipleRoleQuery } from '@/store/api/core/actions.api';

export interface IRoleActions {
	allRoleActions: string[];
	isAllowedRoles: IUseIsRole;
	isLoading: boolean;
	/** Full permission state, including how it was resolved. */
	snapshot: IPermissionSnapshot;
	source: PermissionSource;
	/** False while the answer is still unknown — callers must fail OPEN, not deny. */
	isResolved: boolean;
	roles: readonly string[];
	can: (action: ACTIONTYPE) => boolean;
	canAny: (actions: readonly ACTIONTYPE[]) => boolean;
	canAll: (actions: readonly ACTIONTYPE[]) => boolean;
}

/**
 * Resolves the current user's permitted actions.
 *
 * Fast path: api.core embeds one `action` claim per permitted code in the JWT, so the
 * answer is available synchronously on first render — no request, no skeleton, no
 * flash of a menu that is about to change. That is also the exact claim the backend's
 * `HasActionHandler` enforces on, so the two tiers read the same source.
 *
 * Fallback: tokens minted before those claims shipped carry no `action` key, and are
 * indistinguishable from a role that genuinely holds nothing (see `authority.ts`). Both
 * fall through to `POST /Action/getActionsForMultipleRoles`.
 *
 * The union across a user's roles is deliberate and matches the backend: `TokenService`
 * unions at mint time and `HasActionHandler` succeeds on any single matching claim.
 * Intersecting, or reading only `role[0]`, would make the UI deny things the API
 * permits — the worst available failure mode. Do not "fix" this.
 */
export const useRoleActions = (): IRoleActions => {
	const jwt = useJwtSnapshot();

	const isLoggedIn = ISADMINLOGGED();
	const needsFallback = isLoggedIn && jwt.source !== 'jwt';

	const query = useGetForMultipleRoleQuery({ roles: [...jwt.roles] }, { skip: !needsFallback });

	return useMemo<IRoleActions>(() => {
		let snapshot = jwt;

		if (needsFallback) {
			const fetched = query.isSuccess ? (query.data?.data ?? []) : null;

			snapshot =
				fetched === null
					? // Still loading, or the request failed. Either way we do not have an
						// answer, and an errored fetch is usually an expired session — denying
						// there sends an operator to their administrator for a re-login problem.
						{ ...jwt, isResolved: false, source: 'unresolved' }
					: { actions: buildActionSet(fetched), roles: jwt.roles, source: 'api', isResolved: true };
		}

		return toRoleActionsValue(snapshot, needsFallback && query.isLoading);
	}, [jwt, needsFallback, query.isSuccess, query.isLoading, query.data]);
};
